import { differenceInMilliseconds } from 'date-fns';

// Config
import { selectEntirelyGiftTender } from '../payment/paymentActions';
import {
  SAVE_TIME_TO_DELIVERY_PAGE,
  SAVE_TIME_TO_DELIVERY_METHODS,
  SAVE_TIME_TO_COLLECTION_DATES,
  SAVE_TIME_TO_AUTH0TOKEN,
  DELIVERY_TIMINGS_RECORDED,
  SAVE_TIME_CONTINUE_TO_PAYMENT_CLICKED,
  PAYMENT_TIMINGS_RECORDED,
  CONFIRMATION_TIMINGS_RECORDED,
  SAVE_TIME_PLACE_ORDER_CLICKED,
  SET_CHECKOUT_START_TIME,
  CLEAR_CHECKOUT_START_TIME,
  CHECKOUT_COMPLETION_TIME_RECORDED,
} from '@constants/actionConstants';
import deliveryConstants, { fulfilmentTypes } from '@constants/deliveryConstants';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import { getAppHasCheckoutCompletionTimeRecorded, getAppCheckoutStartTime } from '@redux/reducers/app/appSelector';
import { getOutstandingBalanceSelector, getOrderFormDeliveries } from '@redux/reducers/order-form/orderFormSelector';
import { getSelectedPaymentTypeSelector } from '@redux/reducers/payment/paymentSelector';
import { getUserDefaultAddress, getUserHasSavedCollectionPoints } from '@redux/reducers/user/userSelector';
import { isZeroAmount } from '@utils/index';
import { isClickAndCollectChoice, isDeliveryChoice } from '@utils/delivery/deliveryHelper';
import { sendNewRelicCustomEvent } from '@utils/logging/logging-utils';
import { isEmptyObject } from '@utils/object';

export const shouldRecordDeliveryTimings = () => (dispatch, getState) => {
  const { app: { timestamps } = {}, user: { isSignedIn } = {} } = getState();

  return Boolean(!!timestamps && isSignedIn);
};

export const getPrimaryPaymentType = (orderDetails = {}) => {
  const { payments = [] } = orderDetails;

  const primaryPaymentTypes = [
    paymentTypeConstants.SAVED_PAYMENT_CARD,
    paymentTypeConstants.TOKENIZED_PAYMENT_CARD,
    paymentTypeConstants.APPLE_PAY,
    paymentTypeConstants.GOOGLE_PAY,
    paymentTypeConstants.POS_CREDIT,
    paymentTypeConstants.PAYPAL,
    paymentTypeConstants.KLARNA,
  ];

  const primaryPayment = payments.find((payment) => primaryPaymentTypes.includes(payment.type));

  // paid total or partial order with a primary payment type
  if (primaryPayment) return primaryPayment.type;

  // paid for order entirely with secondary payment types e.g. gift card or voucher
  return 'none';
};

export const recordConfirmationTimings = () => (dispatch, getState) => {
  const { app: { timestamps, confirmationTimingsRecorded } = {}, confirmation: { orderDetails } = {} } =
    getState() || {};

  if (isEmptyObject(timestamps)) return;

  if (confirmationTimingsRecorded) return;

  const { timePlaceOrderClicked } = timestamps;

  const actionName = 'checkout-timing';
  const attributes = {
    action: 'payment-to-order-confirmation-page',
    timeToOrderConfirmationPage: differenceInMilliseconds(Date.now(), timePlaceOrderClicked) / 1000,
    primaryPaymentType: getPrimaryPaymentType(orderDetails),
  };

  sendNewRelicCustomEvent(actionName, attributes);
  dispatch({
    type: CONFIRMATION_TIMINGS_RECORDED,
  });
};

export const recordPaymentTimings = () => (dispatch, getState) => {
  const { timestamps, paymentTimingsRecorded } = getState().app || {};

  if (isEmptyObject(timestamps)) return;

  if (paymentTimingsRecorded) return;

  const { timeContinueToPaymentClicked } = timestamps;

  const actionName = 'checkout-timing';
  const attributes = {
    action: 'delivery-to-payment-page',
    timeToPaymentPage: differenceInMilliseconds(Date.now(), timeContinueToPaymentClicked) / 1000,
  };

  sendNewRelicCustomEvent(actionName, attributes);
  dispatch({
    type: PAYMENT_TIMINGS_RECORDED,
  });
};

export const recordDeliveryTimings =
  ({ action, extraAttributes = {} }) =>
  (dispatch, getState) => {
    const { timestamps, deliveryTimingsRecorded } = getState().app || {};

    if (deliveryTimingsRecorded) return;

    if (!isEmptyObject(timestamps)) {
      const { timeToCheckoutSpinner, timeToReactLoaded, timeToDeliveryPage, timeToAuth0Complete } = timestamps;

      const actionName = 'checkout-timing';
      const attributes = {
        action,
        timeToCheckoutSpinner: timeToCheckoutSpinner / 1000,
        timeToReactLoaded: timeToReactLoaded / 1000,
        timeToDeliveryPage: timeToDeliveryPage / 1000,
        ...(timeToAuth0Complete && { timeToAuth0Complete: timeToAuth0Complete / 1000 }),
        ...extraAttributes,
      };

      sendNewRelicCustomEvent(actionName, attributes);
      dispatch({
        type: DELIVERY_TIMINGS_RECORDED,
      });
    }
  };

export const saveTimeToAuth0TokenTimestamp = () => ({
  type: SAVE_TIME_TO_AUTH0TOKEN,
  timeToAuth0Complete: Date.now(),
});

export const saveTimeToDeliveryPageTimestamp = () => ({
  type: SAVE_TIME_TO_DELIVERY_PAGE,
  timeToDeliveryPage: Date.now(),
});

export const saveTimeToDeliveryMethodsTimestamp = () => ({
  type: SAVE_TIME_TO_DELIVERY_METHODS,
  timeToDeliveryMethods: Date.now(),
});

export const saveTimeToCollectionDatesTimestamp = () => ({
  type: SAVE_TIME_TO_COLLECTION_DATES,
  timeToCollectionDates: Date.now(),
});

export const saveTimeContinueToPaymentClicked = () => ({
  type: SAVE_TIME_CONTINUE_TO_PAYMENT_CLICKED,
  timeContinueToPaymentClicked: Date.now(),
});

export const saveTimePlaceOrderClicked = () => ({
  type: SAVE_TIME_PLACE_ORDER_CLICKED,
  timePlaceOrderClicked: Date.now(),
});

export const setCheckoutStartTime = (timestamp = Date.now()) => ({
  type: SET_CHECKOUT_START_TIME,
  payload: timestamp,
});

export const clearCheckoutStartTime = () => ({
  type: CLEAR_CHECKOUT_START_TIME,
});

export const recordCheckoutCompletionTimeRecorded = () => ({
  type: CHECKOUT_COMPLETION_TIME_RECORDED,
});

export const recordCheckoutArrivalTime = () => (dispatch, getState) => {
  const checkoutStartTime = getAppCheckoutStartTime(getState());

  if (checkoutStartTime) return;

  dispatch(setCheckoutStartTime());
};

const getFulfilmentAttributes = (state) => {
  const deliveries = getOrderFormDeliveries(state);
  const fulfilmentTypeSet = new Set();
  let fulfilmentMethod = null;
  let userHasSavedCollectionPoints = false;
  let userHasDefaultAddress = false;

  deliveries.forEach(({ fulfilment }) => {
    if (!fulfilment) return;

    const { fulfilmentType, collectionInfo } = fulfilment;
    const isClickAndCollect =
      fulfilmentType === fulfilmentTypes.CLICK_AND_COLLECT || fulfilmentType === fulfilmentTypes.COLLECT_PLUS;

    if (isClickAndCollect) {
      userHasSavedCollectionPoints = getUserHasSavedCollectionPoints(state);
    } else {
      userHasDefaultAddress = !!getUserDefaultAddress(state);
    }

    fulfilmentMethod = isClickAndCollect ? deliveryConstants.CLICK_AND_COLLECT : deliveryConstants.DELIVERY;

    const resolvedFulfilmentType = isClickAndCollect ? collectionInfo?.type : fulfilmentType;

    if (resolvedFulfilmentType) {
      fulfilmentTypeSet.add(resolvedFulfilmentType);
    }
  });

  return {
    fulfilmentType: Array.from(fulfilmentTypeSet).join(','),
    fulfilmentMethod,
    isMultiBatch: deliveries.length > 1,
    userHasDefaultAddress,
    userHasSavedCollectionPoints,
  };
};

const getPaymentMethodType = (state) => {
  if (!isZeroAmount(getOutstandingBalanceSelector(state))) {
    return getSelectedPaymentTypeSelector(state) || '';
  }

  const tenderType = selectEntirelyGiftTender(state);

  if (tenderType === paymentTypeConstants.GIFT_CARD) {
    return paymentTypeConstants.GIFT_CARD;
  }

  if (tenderType === paymentTypeConstants.GIFT_VOUCHER) {
    return paymentTypeConstants.GIFT_VOUCHER;
  }

  return paymentTypeConstants.GIFT_OPTIONS;
};

export const recordCheckoutCompletionTime = () => (dispatch, getState) => {
  const state = getState();
  const checkoutStartTime = getAppCheckoutStartTime(state);
  const checkoutCompletionTimeRecorded = getAppHasCheckoutCompletionTimeRecorded(state);

  if (!checkoutStartTime || checkoutCompletionTimeRecorded) return;

  sendNewRelicCustomEvent('checkout-time-to-order-confirmation', {
    ...getFulfilmentAttributes(state),
    paymentMethodType: getPaymentMethodType(state),
    timeInCheckoutMs: Date.now() - checkoutStartTime,
  });

  dispatch(recordCheckoutCompletionTimeRecorded());
  dispatch(clearCheckoutStartTime());
};

export const recordCollectionDateTimings = () => (dispatch, getState) => {
  if (!dispatch(shouldRecordDeliveryTimings())) return;

  const { app: { timestamps: { timeContinueToCheckoutClicked } = {} } = {} } = getState();

  dispatch(saveTimeToCollectionDatesTimestamp());

  dispatch(
    recordDeliveryTimings({
      action: 'basket-to-collection-dates',
      extraAttributes: {
        timeToCollectionDates: (Date.now() - timeContinueToCheckoutClicked) / 1000,
      },
    }),
  );
};

export const recordDeliveryMethodTimings = () => (dispatch, getState) => {
  if (!dispatch(shouldRecordDeliveryTimings())) return;

  const {
    app: { timestamps: { timeContinueToCheckoutClicked } = {} },
    delivery: { deliveries = [] } = {},
  } = getState();

  dispatch(saveTimeToDeliveryMethodsTimestamp());

  let deliveryType;

  if (deliveries.length === 1) deliveryType = deliveries[0].deliveryType;

  dispatch(
    recordDeliveryTimings({
      action: 'basket-to-delivery-methods',
      extraAttributes: {
        timeToDeliveryMethods: (Date.now() - timeContinueToCheckoutClicked) / 1000,
        deliveryType,
      },
    }),
  );
};

export const recordInitDeliveryPageTimings = () => (dispatch, getState) => {
  if (!dispatch(shouldRecordDeliveryTimings())) return;

  const {
    delivery: { selectedDeliveryChoiceId },
    user: { collectionPointsSaved, defaultAddress },
  } = getState();

  dispatch(saveTimeToDeliveryPageTimestamp());

  const isCollection = isClickAndCollectChoice(selectedDeliveryChoiceId);
  const isDelivery = isDeliveryChoice(selectedDeliveryChoiceId);

  if (isDelivery && defaultAddress) {
    // timings will be recorded further along
    return;
  }

  if (isCollection && collectionPointsSaved) {
    // timings will be recorded further along
    return;
  }

  dispatch(
    recordDeliveryTimings({
      action: isCollection ? 'basket-to-collection' : 'basket-to-delivery',
    }),
  );
};
